import { MESApiService, type spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type { GasConsumptionData, GasRow } from "../models/GasConsumption";

const round2 = (n: number) =>
	Number.isFinite(n) ? Number.parseFloat(n.toFixed(2)) : 0;

export interface GetGasConsumptionParams {
	start: string;
	end: string;
	signal?: AbortSignal;
}

export const getGasConsumption = async ({
	start,
	end,
	signal,
}: GetGasConsumptionParams): Promise<GasConsumptionData> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "start", value: start },
		{ name: "end", value: end },
	];

	const spName = "[MILL].[GetGasConsumptionDataWeb]";
	const resp = await apiService.callV2(spName, parameters, signal);

	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching gas consumption");
	}

	// Result set 1 carries the daily rows plus a trailing summary row labeled
	// 'Total' (legacy CONS.GetNaturalGas appends it via UNION ALL). Split it out
	// so the grid shows only daily rows and the summary band shows the total.
	const allRows = get(resp, "data.tables[0].rows", []) as GasRow[];
	const totalRow = allRows.find((r) => r.Date === "Total");
	const rows = allRows.filter((r) => r.Date !== "Total");
	// MaxValue is a raw FLOAT(53) → strip IEEE-754 noise (e.g. 2000.0999999).
	const max = get(resp, "data.tables[1].rows[0].MaxValue", 0) as number;

	return {
		rows,
		total: round2(totalRow?.Value ?? 0),
		max: round2(max ?? 0),
	};
};
